import type { HttpError } from '@/api/request'
import Toast from '@/components/Toast'
import MessageBox from '@/components/MessageBox'
import { logger } from '@/utils/logger'

/**
 * Toast 能承载的信息上限（与「Toast 文案 ≤20 字」的规范保持一致）。
 *
 * 超过该长度、或含并列分隔符/换行的消息，5 秒内读不完且不可复制，
 * 一律升级为常驻对话框，否则用户错过即永久丢失。
 */
const TOAST_MAX_MESSAGE_LENGTH = 20
const MULTI_ITEM_SEPARATORS = ['；', '\n']

/**
 * 升级弹窗的合并窗口。
 *
 * 弹窗是单例，连续触发会互相覆盖——批量操作时若每条错误都单独弹一次，
 * 用户最终只看到最后一条。短时间窗内的错误合并为一条多行对话框，避免信息丢失。
 * 副作用是同一错误被多层 catch 重复上报时只会展示一次。
 */
const ALERT_BATCH_WINDOW_MS = 400
/** 合并等待上限：错误持续以小于合并窗口的间隔到达时，避免定时器被无限续期 */
const ALERT_MAX_WAIT_MS = 2000

interface PendingAlert {
  text: string
  title: string
}

let pendingAlerts: PendingAlert[] = []
let alertFlushTimer: ReturnType<typeof setTimeout> | null = null
let firstEnqueuedAt = 0

function shouldEscalateToDialog(message: string): boolean {
  if (message.length > TOAST_MAX_MESSAGE_LENGTH) {
    return true
  }

  return MULTI_ITEM_SEPARATORS.some((separator) => message.includes(separator))
}

function flushPendingAlerts(): void {
  const batch = pendingAlerts
  pendingAlerts = []
  alertFlushTimer = null

  if (batch.length === 0) {
    return
  }

  firstEnqueuedAt = 0

  const merged = batch.map((item) => item.text).join('\n')
  const title = batch.length === 1 ? batch[0].title : `操作未完成（${batch.length} 项）`

  void MessageBox.alert(merged, title, { type: 'danger' })
}

function alertEscalated(message: string, title: string): void {
  const now = Date.now()
  if (pendingAlerts.length === 0) {
    firstEnqueuedAt = now
  }

  if (!pendingAlerts.some((item) => item.text === message)) {
    pendingAlerts.push({ text: message, title })
  }

  if (alertFlushTimer) {
    clearTimeout(alertFlushTimer)
  }

  const remaining = Math.max(0, ALERT_MAX_WAIT_MS - (now - firstEnqueuedAt))
  alertFlushTimer = setTimeout(flushPendingAlerts, Math.min(ALERT_BATCH_WINDOW_MS, remaining))
}

/** 按消息体量自动选择提示载体：简短走 Toast，长文案/多条目走常驻对话框 */
export function reportErrorMessage(message: string, title = '操作未完成'): void {
  if (shouldEscalateToDialog(message)) {
    alertEscalated(message, title)
    return
  }

  Toast.error(message)
}

enum ErrorCategory {
  NETWORK = 'NETWORK',
  BUSINESS = 'BUSINESS',
  SERVER = 'SERVER',
  AUTHENTICATION = 'AUTH',
  VALIDATION = 'VALIDATION',
  UNKNOWN = 'UNKNOWN'
}

interface ErrorClassificationResult {
  category: ErrorCategory
  originalError: unknown
}

interface ErrorHandlingOptions {
  errorMessage?: string
  showToast?: boolean
  onError?: (error: unknown) => void
  context?: string
}

const SILENT_ERROR_FLAG = Symbol('silentError')

/**
 * 标记错误为静默：已有其他持久化展示渠道（如排课向导第 5 步问题面板）时，
 * withErrorHandling 不再弹 Toast，仅记录日志并继续抛出。
 */
export function markErrorSilent<T>(error: T): T {
  if (error != null && typeof error === 'object') {
    Object.defineProperty(error, SILENT_ERROR_FLAG, { value: true, enumerable: false })
  }
  return error
}

export function isSilentError(error: unknown): boolean {
  return (
    error != null
    && typeof error === 'object'
    && (error as Record<PropertyKey, unknown>)[SILENT_ERROR_FLAG] === true
  )
}

const DEFAULT_ERROR_MESSAGES: Record<ErrorCategory, string> = {
  [ErrorCategory.NETWORK]: '网络连接异常，请检查您的网络设置后重试',
  [ErrorCategory.BUSINESS]: '操作失败，请检查输入信息是否正确',
  [ErrorCategory.SERVER]: '服务器暂时不可用，请稍后再试',
  [ErrorCategory.AUTHENTICATION]: '登录状态已失效，请重新登录',
  [ErrorCategory.VALIDATION]: '数据校验未通过，请检查输入内容',
  [ErrorCategory.UNKNOWN]: '发生了未知错误，请稍后重试'
}

const STATUS_CODE_CATEGORY_MAP: Record<number, ErrorCategory> = {
  0: ErrorCategory.NETWORK,
  400: ErrorCategory.VALIDATION,
  401: ErrorCategory.AUTHENTICATION,
  403: ErrorCategory.AUTHENTICATION,
  404: ErrorCategory.BUSINESS,
  408: ErrorCategory.NETWORK,
  409: ErrorCategory.BUSINESS,
  422: ErrorCategory.VALIDATION,
  429: ErrorCategory.BUSINESS,
  500: ErrorCategory.SERVER,
  502: ErrorCategory.SERVER,
  503: ErrorCategory.SERVER,
  504: ErrorCategory.SERVER
}

function classifyError(error: unknown): ErrorClassificationResult {
  if (error == null) {
    return {
      category: ErrorCategory.UNKNOWN,
      originalError: error
    }
  }

  if (isHttpError(error)) {
    const category =
      error.code === 'NETWORK_ERROR'
        ? ErrorCategory.NETWORK
        : STATUS_CODE_CATEGORY_MAP[error.status] ??
          (hasValidationErrors(error) ? ErrorCategory.VALIDATION : ErrorCategory.UNKNOWN)

    return {
      category,
      originalError: error
    }
  }

  if (error instanceof TypeError) {
    const message = error.message.toLowerCase()
    const networkKeywords = ['fetch', 'network', 'failed to load', 'failed to fetch', 'net::err_', 'abort']

    return {
      category: networkKeywords.some((keyword) => message.includes(keyword))
        ? ErrorCategory.NETWORK
        : ErrorCategory.UNKNOWN,
      originalError: error
    }
  }

  if (error instanceof Error) {
    const statusCodeMatch = error.message.match(/status\s+code\s+(\d{3})/i)
    if (statusCodeMatch) {
      const statusCode = Number.parseInt(statusCodeMatch[1], 10)

      return {
        category: STATUS_CODE_CATEGORY_MAP[statusCode] ?? ErrorCategory.UNKNOWN,
        originalError: error
      }
    }

    return {
      category: ErrorCategory.UNKNOWN,
      originalError: error
    }
  }

  return {
    category: ErrorCategory.UNKNOWN,
    originalError: error
  }
}

export function formatErrorMessage(error: unknown, customMessage?: string): string {
  const detailedMessage = extractDisplayMessage(error)
  if (detailedMessage) {
    return detailedMessage
  }

  // 网络类错误优先展示可读中文，避免 Failed to fetch 被当成技术信息后落到笼统自定义文案
  if (isHttpError(error) && error.code === 'NETWORK_ERROR') {
    return DEFAULT_ERROR_MESSAGES[ErrorCategory.NETWORK]
  }

  if (customMessage && customMessage.trim()) {
    return customMessage.trim()
  }

  const classification = classifyError(error)
  return DEFAULT_ERROR_MESSAGES[classification.category]
}

function extractDisplayMessage(error: unknown): string | null {
  if (isHttpError(error)) {
    const errorDiagnostics = formatValidationErrors(error.errors, 'error')
    if (errorDiagnostics && !isTechnicalMessage(errorDiagnostics)) {
      return errorDiagnostics
    }

    const safeMessage = extractSafeMessage(error.message)
    if (safeMessage && !isTechnicalMessage(safeMessage)) {
      return safeMessage
    }

    const warningDiagnostics = formatValidationErrors(error.errors, 'warning')
    if (warningDiagnostics && !isTechnicalMessage(warningDiagnostics)) {
      return warningDiagnostics
    }

    return null
  }

  if (error instanceof Error) {
    if (error.cause != null) {
      const fromCause = extractDisplayMessage(error.cause)
      if (fromCause) {
        return fromCause
      }
    }

    const safeMessage = extractSafeMessage(error.message)
    if (safeMessage && !isTechnicalMessage(safeMessage)) {
      return safeMessage
    }

    return null
  }

  return null
}

function logError(error: unknown, context?: string): void {
  const classification = classifyError(error)
  const timestamp = new Date().toISOString()

  logger.error(
    `${context ? `[${context}] ` : ''}${classification.category} - ${extractErrorMessage(error)}`,
    '\n  Original:',
    error,
    '\n  Time:',
    timestamp,
    '\n  Details:',
    {
      category: classification.category,
      context: context || 'unknown',
      timestamp,
      errorType: getErrorTypeName(error),
      message: extractErrorMessage(error),
      stack: import.meta.env.DEV && error instanceof Error ? error.stack : undefined,
      ...(isHttpError(error) && {
        status: error.status,
        code: error.code,
        traceId: error.traceId || undefined,
        errors: error.errors
      })
    }
  )
}

export async function withErrorHandling<T>(
  apiCall: () => Promise<T>,
  options: ErrorHandlingOptions = {}
): Promise<T> {
  const {
    errorMessage,
    showToast = true,
    onError,
    context
  } = options

  try {
    return await apiCall()
  } catch (error: unknown) {
    logError(error, context)

    if (showToast && !isSilentError(error)) {
      // context 拼进标题：所有升级弹窗不再一律是「操作未完成」，用户能看清是哪个环节失败
      reportErrorMessage(
        formatErrorMessage(error, errorMessage),
        context ? `${context}失败` : undefined
      )
    }

    if (onError) {
      try {
        onError(error)
      } catch (callbackError) {
        logger.warn('[error-handler] onError callback threw an error:', callbackError)
      }
    }

    throw error
  }
}

function isHttpError(error: unknown): error is HttpError {
  return (
    error != null &&
    typeof error === 'object' &&
    'name' in error &&
    (error as HttpError).name === 'HttpError' &&
    'status' in error &&
    'code' in error &&
    'message' in error
  )
}

function hasValidationErrors(error: HttpError): boolean {
  if (!error.errors) {
    return false
  }

  if (Array.isArray(error.errors)) {
    return error.errors.some((item) => {
      if (typeof item === 'string' && item.trim().length > 0) {
        return true
      }
      return item != null
        && typeof item === 'object'
        && 'message' in item
        && typeof (item as { message?: unknown }).message === 'string'
        && (item as { message: string }).message.trim().length > 0
    })
  }

  return Object.values(error.errors).some(
    (messages) =>
      Array.isArray(messages) &&
      messages.some((message) => typeof message === 'string' && message.trim().length > 0)
  )
}

type DiagnosticLike = {
  message?: unknown
  severity?: unknown
}

function diagnosticSeverity(item: unknown): string {
  if (!item || typeof item !== 'object' || !('severity' in item)) {
    return 'error'
  }

  const severity = (item as DiagnosticLike).severity
  return typeof severity === 'string' ? severity.toLowerCase() : 'error'
}

function extractDiagnosticMessages(
  errors: HttpError['errors'],
  severityFilter: 'error' | 'warning' | 'all' = 'all'
): string[] {
  if (!errors) {
    return []
  }

  if (!Array.isArray(errors)) {
    return []
  }

  const messages: string[] = []

  for (const item of errors) {
    if (typeof item === 'string' && item.trim().length > 0) {
      if (severityFilter === 'all' || severityFilter === 'error') {
        messages.push(item.trim())
      }
      continue
    }

    if (!item || typeof item !== 'object' || !('message' in item)) {
      continue
    }

    const message = (item as DiagnosticLike).message
    if (typeof message !== 'string' || message.trim().length === 0) {
      continue
    }

    const severity = diagnosticSeverity(item)
    if (severityFilter !== 'all' && severity !== severityFilter) {
      continue
    }

    messages.push(message.trim())
  }

  return [...new Set(messages)]
}

function formatValidationErrors(
  errors: HttpError['errors'],
  severityFilter: 'error' | 'warning' | 'all' = 'all'
): string | null {
  if (!errors) {
    return null
  }

  if (Array.isArray(errors)) {
    const messages = extractDiagnosticMessages(errors, severityFilter)
      .filter((message) => !isTechnicalMessage(message))
    if (messages.length === 0) {
      return null
    }

    if (severityFilter === 'warning' && messages.length > 3) {
      const display = messages.slice(0, 3)
      display.push(`另有 ${messages.length - 3} 条类似提示`)
      return display.join('；')
    }

    return messages.join('；')
  }

  const entries = Object.entries(errors).flatMap(([, messages]) => {
    if (!Array.isArray(messages)) {
      return []
    }

    return messages
      .filter((message): message is string => typeof message === 'string' && message.trim().length > 0)
      .map((message) => message.trim())
      .filter((message) => !isTechnicalMessage(message))
  })

  return entries.length > 0 ? [...new Set(entries)].join('；') : null
}

function extractSafeMessage(message: string): string {
  if (!message || typeof message !== 'string') {
    return ''
  }

  let safe = message.trim()
  const sensitivePatterns = [
    /traceId[:\s]*[\w-]+/gi,
    /request id[:\s]*[\w-]+/gi,
    /at\s+.*?\.(js|ts|vue):\d+:\d+/gi,
    /sql\s*state.*$/gim,
    /\d{1,3}(?:\.\d{1,3}){3}/g,
  ]

  for (const pattern of sensitivePatterns) {
    safe = safe.replace(pattern, '[已隐藏]')
  }

  return safe
}

function isTechnicalMessage(message: string): boolean {
  if (!message) {
    return true
  }

  // 已有中文业务说明的长文案（如校验失败）不应被当成技术信息吞掉
  if (/[\u4e00-\u9fff]/.test(message)) {
    return false
  }

  // 纯英文开发者文案不要直接展示给学校老师，改走分类后的中文兜底
  if (/[A-Za-z]/.test(message)) {
    return true
  }

  const technicalPatterns = [
    /at\s+.*?\.(js|ts|vue)/,
    /undefined\s+(is|not)\s+/i,
    /cannot\s+(read|set)\s+property/i,
    /Unexpected\s+token/i,
    /circular\s+structure/i,
    /Network(?:Error|Request)\s+failed/i,
    /Failed\s+to\s+fetch/i,
    /Load\s+failed/i,
  ]

  return technicalPatterns.some((pattern) => pattern.test(message)) || message.length > 200
}

function extractErrorMessage(error: unknown): string {
  if (error == null) {
    return 'null or undefined error'
  }

  if (typeof error === 'string') {
    return error
  }

  if (error instanceof Error) {
    return error.message || error.name || 'Unknown Error'
  }

  if (typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message)
  }

  try {
    return JSON.stringify(error)
  } catch {
    return String(error)
  }
}

function getErrorTypeName(error: unknown): string {
  if (error == null) {
    return 'null'
  }

  if (isHttpError(error)) {
    return 'HttpError'
  }

  if (error instanceof Error) {
    return error.constructor.name
  }

  return typeof error
}
